import { Controller } from "@hotwired/stimulus"
import type { TurboSubmitStartEvent, TurboSubmitEndEvent } from "@hotwired/turbo"
import SelectionToolbarController from "./selection_toolbar_controller"

/**
 * Controller for appending selected IDs to form submissions.
 * Works with SelectionToolbarController via Stimulus outlets.
 *
 * This controller is focused and composable - it ONLY handles:
 * - Appending selected IDs from SelectionToolbarController to form body
 * - Clearing selection on successful submit
 *
 * Use with confirm-action controller for confirmation dialogs:
 *
 *   <form data-controller="confirm-action bulk-selection"
 *         data-confirm-action-type-value="bulkDelete"
 *         data-confirm-action-item-value="users"
 *         data-bulk-selection-selection-toolbar-outlet="[data-controller*='selection-toolbar']"
 *         data-action="turbo:before-fetch-request->confirm-action#confirm
 *                      turbo:submit-start->bulk-selection#appendIds
 *                      turbo:submit-end->bulk-selection#handleComplete">
 *     <button type="submit">Delete Selected</button>
 *   </form>
 */
export default class BulkSelectionController extends Controller {
  static outlets = ["selection-toolbar"]
  static values = {
    param: { type: String, default: "ids[]" },
    clearOnSuccess: { type: Boolean, default: true },
  }

  declare selectionToolbarOutlet: SelectionToolbarController
  declare paramValue: string
  declare clearOnSuccessValue: boolean

  appendIds(event: TurboSubmitStartEvent): void {
    this.selectionToolbarOutlet.selectedIds().forEach((id) => {
      event.detail.formSubmission.fetchRequest.body.append(this.paramValue, id)
    })
  }

  handleComplete(event: TurboSubmitEndEvent): void {
    if (this.clearOnSuccessValue && event.detail.success) {
      this.selectionToolbarOutlet.clearSelected()
    }
  }
}
